'use client';

/**
 * GlassSurface Component - Liquid Glass Effect
 * ============================================
 *
 * Creates the liquid glass distortion effect with chromatic aberration.
 *
 * HOW THE EFFECT WORKS:
 * 1. An SVG displacement map is generated with red/blue gradients
 * 2. The backdrop-filter uses this map to displace pixels
 * 3. Each RGB channel is displaced by a different amount
 * 4. This creates the "chromatic aberration" - color fringing at edges
 * 5. The channels are recombined with screen blend mode
 *
 * BROWSER SUPPORT:
 * - Chromium (Chrome, Edge): Full liquid glass with chromatic aberration
 * - Safari/iOS: Falls back to iOS-style liquid glass (blur + gradient + shine)
 * - Firefox: Falls back to iOS-style liquid glass
 *
 * The Safari fallback uses the same visual language as Apple's iOS liquid glass:
 * - backdrop-filter blur with -webkit- prefix
 * - Gradient overlays for depth
 * - Inner glow/shine effects
 * - Subtle borders
 */

import React, { useEffect, useRef, useId, useState } from 'react';

/**
 * Detect if browser supports SVG filters in backdrop-filter
 * Only Chromium browsers support this feature
 */
const checkSVGFilterSupport = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check for Chrome/Chromium (not Safari, not Firefox)
  const isChromium = !!(window as Window & { chrome?: unknown }).chrome;
  const isEdge = navigator.userAgent.includes('Edg/');
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isFirefox = navigator.userAgent.includes('Firefox');
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // SVG filters in backdrop-filter only work in Chromium
  return (isChromium || isEdge) && !isSafari && !isFirefox && !isIOS;
};

export interface GlassSurfaceProps {
  children?: React.ReactNode;
  width?: number | string;
  height?: number | string;
  borderRadius?: number;
  borderWidth?: number;
  brightness?: number;
  opacity?: number;
  blur?: number;
  displace?: number;
  backgroundOpacity?: number;
  saturation?: number;
  distortionScale?: number;
  redOffset?: number;
  greenOffset?: number;
  blueOffset?: number;
  xChannel?: 'R' | 'G' | 'B';
  yChannel?: 'R' | 'G' | 'B';
  mixBlendMode?: string;
  className?: string;
  style?: React.CSSProperties;
}

const GlassSurface: React.FC<GlassSurfaceProps> = ({
  children,
  width = 200,
  height = 80,
  borderRadius = 50,
  borderWidth = 0.07,
  brightness = 50,
  opacity = 0.93,
  blur = 11,
  displace = 0,
  backgroundOpacity = 0.1,
  saturation = 1,
  distortionScale = -180,
  redOffset = 0,
  greenOffset = 10,
  blueOffset = 20,
  xChannel = 'R',
  yChannel = 'G',
  mixBlendMode = 'difference',
  className = '',
  style = {}
}) => {
  const uniqueId = useId().replace(/:/g, '-');
  const filterId = `glass-filter-${uniqueId}`;
  const redGradId = `red-grad-${uniqueId}`;
  const blueGradId = `blue-grad-${uniqueId}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const feImageRef = useRef<SVGFEImageElement>(null);
  const redChannelRef = useRef<SVGFEDisplacementMapElement>(null);
  const greenChannelRef = useRef<SVGFEDisplacementMapElement>(null);
  const blueChannelRef = useRef<SVGFEDisplacementMapElement>(null);
  const gaussianBlurRef = useRef<SVGFEGaussianBlurElement>(null);

  const [isMounted, setIsMounted] = useState(false);
  const [supportsSVGFilter, setSupportsSVGFilter] = useState(true);

  // Generate the SVG displacement map
  const generateDisplacementMap = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    const actualWidth = rect?.width || 400;
    const actualHeight = rect?.height || 200;
    const edgeSize = Math.min(actualWidth, actualHeight) * (borderWidth * 0.5);

    // For feDisplacementMap:
    // - Red channel (R) controls X displacement when xChannelSelector="R"
    // - Green channel (G) controls Y displacement when yChannelSelector="G"
    // - Value 128 = no displacement (neutral center)
    // - Value < 128 = negative displacement (left/up)
    // - Value > 128 = positive displacement (right/down)
    //
    // BUBBLE EFFECT: Edges bend outward
    // - Left edge: R > 128 → with negative scale, pushes content LEFT
    // - Right edge: R < 128 → with negative scale, pushes content RIGHT
    // - Top edge: G > 128 → with negative scale, pushes content UP
    // - Bottom edge: G < 128 → with negative scale, pushes content DOWN
    //
    // Single gradient approach: R and G in one horizontal gradient with G=128 constant
    // Then overlay vertical gradient for G channel variation
    const svgContent = `
      <svg viewBox="0 0 ${actualWidth} ${actualHeight}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <!-- Horizontal gradient: R varies (176→128→80), G fixed at 128 -->
          <!-- With negative distortionScale: R>128 at left pushes left, R<128 at right pushes right -->
          <linearGradient id="hGrad-${uniqueId}" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stop-color="rgb(176,128,128)"/>
            <stop offset="20%" stop-color="rgb(128,128,128)"/>
            <stop offset="80%" stop-color="rgb(128,128,128)"/>
            <stop offset="100%" stop-color="rgb(80,128,128)"/>
          </linearGradient>
          <!-- Vertical gradient: G varies (176→128→80), R fixed at 128, use multiply to combine -->
          <linearGradient id="vGrad-${uniqueId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="rgb(255,176,255)"/>
            <stop offset="20%" stop-color="rgb(255,128,255)"/>
            <stop offset="80%" stop-color="rgb(255,128,255)"/>
            <stop offset="100%" stop-color="rgb(255,80,255)"/>
          </linearGradient>
        </defs>
        <!-- Base with horizontal gradient -->
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#hGrad-${uniqueId})"/>
        <!-- Overlay vertical gradient using multiply (R*1=R, G*G/255, B doesn't matter) -->
        <rect x="0" y="0" width="${actualWidth}" height="${actualHeight}" rx="${borderRadius}" fill="url(#vGrad-${uniqueId})" style="mix-blend-mode: multiply"/>
        <!-- Center neutral area with blur for smooth glass look -->
        <rect x="${edgeSize}" y="${edgeSize}" width="${actualWidth - edgeSize * 2}" height="${actualHeight - edgeSize * 2}" rx="${Math.max(0, borderRadius - edgeSize)}" fill="hsl(0 0% ${brightness}% / ${opacity})" style="filter:blur(${blur}px)" />
      </svg>
    `;

    return `data:image/svg+xml,${encodeURIComponent(svgContent)}`;
  };

  const updateDisplacementMap = () => {
    if (feImageRef.current) {
      feImageRef.current.setAttribute('href', generateDisplacementMap());
    }
  };

  // Mark as mounted after hydration and check browser support
  useEffect(() => {
    setIsMounted(true);
    setSupportsSVGFilter(checkSVGFilterSupport());
  }, []);

  // Update filter parameters
  useEffect(() => {
    if (!isMounted) return;

    updateDisplacementMap();

    [
      { ref: redChannelRef, offset: redOffset },
      { ref: greenChannelRef, offset: greenOffset },
      { ref: blueChannelRef, offset: blueOffset }
    ].forEach(({ ref, offset }) => {
      if (ref.current) {
        ref.current.setAttribute('scale', (distortionScale + offset).toString());
        ref.current.setAttribute('xChannelSelector', xChannel);
        ref.current.setAttribute('yChannelSelector', yChannel);
      }
    });

    if (gaussianBlurRef.current) {
      gaussianBlurRef.current.setAttribute('stdDeviation', displace.toString());
    }
  }, [
    isMounted,
    borderRadius,
    borderWidth,
    brightness,
    opacity,
    blur,
    displace,
    distortionScale,
    redOffset,
    greenOffset,
    blueOffset,
    xChannel,
    yChannel,
    mixBlendMode
  ]);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current || !isMounted) return;

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateDisplacementMap);
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [isMounted]);

  // Initial update after mount
  useEffect(() => {
    if (isMounted) {
      requestAnimationFrame(updateDisplacementMap);
    }
  }, [isMounted, width, height]);

  // iOS/Safari liquid glass fallback styles
  const safariFallbackStyles: React.CSSProperties = {
    background: `linear-gradient(135deg, rgba(255,255,255,${0.15 + backgroundOpacity}) 0%, rgba(255,255,255,${0.05 + backgroundOpacity}) 50%, rgba(255,255,255,${0.1 + backgroundOpacity}) 100%)`,
    backdropFilter: `blur(${blur + 4}px) saturate(${saturation * 180}%)`,
    WebkitBackdropFilter: `blur(${blur + 4}px) saturate(${saturation * 180}%)`,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: `
      inset 0 1px 1px rgba(255, 255, 255, 0.15),
      inset 0 -1px 1px rgba(255, 255, 255, 0.05),
      0 8px 32px rgba(0, 0, 0, 0.15),
      0 2px 8px rgba(0, 0, 0, 0.1)
    `,
  };

  // Chromium SVG filter styles (full liquid glass with chromatic aberration)
  const chromiumStyles: React.CSSProperties = {
    background: `hsl(0 0% 100% / ${backgroundOpacity})`,
    backdropFilter: `url(#${filterId}) saturate(${saturation})`,
    WebkitBackdropFilter: `url(#${filterId}) saturate(${saturation})`,
    boxShadow: `
      0 0 2px 1px color-mix(in oklch, black, transparent 85%) inset,
      0 0 10px 4px color-mix(in oklch, black, transparent 90%) inset,
      0px 4px 16px rgba(17, 17, 26, 0.05),
      0px 8px 24px rgba(17, 17, 26, 0.05),
      0px 16px 56px rgba(17, 17, 26, 0.05)
    `,
  };

  const containerStyles: React.CSSProperties = {
    ...style,
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    borderRadius: `${borderRadius}px`,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    // Apply appropriate styles based on mount state and browser support
    ...(isMounted
      ? (supportsSVGFilter ? chromiumStyles : safariFallbackStyles)
      : {
          // Initial render styles (same on server and client)
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }
    ),
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={containerStyles}
      suppressHydrationWarning
    >
      {/* Safari/iOS shine overlay - creates the liquid glass shine effect */}
      {isMounted && !supportsSVGFilter && (
        <>
          {/* Top shine gradient */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: `${borderRadius}px`,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 50%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          {/* Edge highlight */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: `${borderRadius}px`,
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -1px 0 rgba(255,255,255,0.1)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
        </>
      )}

      {/* SVG Filter Definition - This is the magic! (Chromium only) */}
      <svg
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          opacity: 0,
          zIndex: -1
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter
            id={filterId}
            colorInterpolationFilters="sRGB"
            x="0%"
            y="0%"
            width="100%"
            height="100%"
          >
            {/* Load the displacement map */}
            <feImage
              ref={feImageRef}
              x="0"
              y="0"
              width="100%"
              height="100%"
              preserveAspectRatio="none"
              result="map"
            />

            {/* Displace RED channel */}
            <feDisplacementMap
              ref={redChannelRef}
              in="SourceGraphic"
              in2="map"
              scale={distortionScale + redOffset}
              xChannelSelector={xChannel}
              yChannelSelector={yChannel}
              result="dispRed"
            />
            <feColorMatrix
              in="dispRed"
              type="matrix"
              values="1 0 0 0 0
                      0 0 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
              result="red"
            />

            {/* Displace GREEN channel */}
            <feDisplacementMap
              ref={greenChannelRef}
              in="SourceGraphic"
              in2="map"
              scale={distortionScale + greenOffset}
              xChannelSelector={xChannel}
              yChannelSelector={yChannel}
              result="dispGreen"
            />
            <feColorMatrix
              in="dispGreen"
              type="matrix"
              values="0 0 0 0 0
                      0 1 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
              result="green"
            />

            {/* Displace BLUE channel */}
            <feDisplacementMap
              ref={blueChannelRef}
              in="SourceGraphic"
              in2="map"
              scale={distortionScale + blueOffset}
              xChannelSelector={xChannel}
              yChannelSelector={yChannel}
              result="dispBlue"
            />
            <feColorMatrix
              in="dispBlue"
              type="matrix"
              values="0 0 0 0 0
                      0 0 0 0 0
                      0 0 1 0 0
                      0 0 0 1 0"
              result="blue"
            />

            {/* Combine all channels with screen blend */}
            <feBlend in="red" in2="green" mode="screen" result="rg" />
            <feBlend in="rg" in2="blue" mode="screen" result="output" />

            {/* Optional blur for smoothing */}
            <feGaussianBlur
              ref={gaussianBlurRef}
              in="output"
              stdDeviation={displace}
            />
          </filter>
        </defs>
      </svg>

      {/* Content */}
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px',
          position: 'relative',
          zIndex: 10
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default GlassSurface;
